/**
 * Product Data Types
 * 
 * Type definitions for product entry form data and related structures.
 * Based on design document data models.
 */

export interface YMMRestriction {
  make: string;
  model: string;
  yearFrom: number;
  yearTo: number;
}

export interface ProductAttribute {
  id: number;
  name: string;
  value: string;
  visible: boolean;
}

export interface ImageData {
  file?: File;
  preview: string;
  uploaded?: boolean;
  mediaId?: number;
}

export type StockStatus = 'instock' | 'outofstock' | 'onbackorder';

export interface ProductFormData {
  // Basic Info
  name: string;
  description: string;
  shortDescription: string;
  regularPrice: string;
  salePrice: string;
  stockQuantity: number;
  stockStatus: StockStatus;
  
  // SKU Management
  internalSku: string;
  supplierReorderUrl: string;
  woocommerceSku: string;
  skuPrefix: string;
  skuBrand: string;
  
  // Categories & Brand
  categoryIds: number[];
  brandId: number | null;
  
  // Attributes
  attributes: ProductAttribute[];
  
  // YMM Fitment
  ymmRestrictions: YMMRestriction[];
  
  // Images
  images: ImageData[];
}

/**
 * Fields that can be retained between product entries
 */
export type RetainableField = keyof Omit<ProductFormData, 'images'>;

/**
 * Set of fields marked for retention
 */
export type RetainedFields = Set<RetainableField>;

/**
 * Default/empty form values
 */
export const DEFAULT_FORM_DATA: ProductFormData = {
  name: '',
  description: '',
  shortDescription: '',
  regularPrice: '',
  salePrice: '',
  stockQuantity: 0,
  stockStatus: 'instock',
  internalSku: '',
  supplierReorderUrl: '',
  woocommerceSku: '',
  skuPrefix: '',
  skuBrand: '',
  categoryIds: [],
  brandId: null,
  attributes: [],
  ymmRestrictions: [],
  images: [],
};

/**
 * Action types for form submission
 */
export type SubmitAction = 'create' | 'createAndAddNew' | 'createNew';
